import * as Sentry from '@sentry/react';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
  deleteDoc,
  doc,
  Timestamp
} from 'firebase/firestore';
import { db, storage, auth } from '../firebase/firebase';
import userService from './user.service';

const IMAGES_COLLECTION = 'images';
const STORAGE_PATH = 'cake-images';
const MAX_IMAGE_SIZE = 2 * 1024 * 1024; // 2MB max for print quality
const JPEG_QUALITY = 0.85; // 85% quality for print-ready images
const CLEANUP_MONTHS = 12; // Delete images older than 12 months

/**
 * Compress an image file to JPEG with specified quality
 * @param {File} file - The image file to compress
 * @param {number} maxWidth - Maximum width (default 2000px for print quality)
 * @param {number} quality - JPEG quality (0-1)
 * @returns {Promise<Blob>} - Compressed image blob
 */
async function compressImage(file, maxWidth = 2000, quality = JPEG_QUALITY) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    img.onload = () => {
      let { width, height } = img;

      // Scale down if wider than maxWidth
      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }

      canvas.width = width;
      canvas.height = height;

      // Draw image on canvas
      ctx.drawImage(img, 0, 0, width, height);

      // Convert to blob
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Failed to compress image'));
          }
        },
        'image/jpeg',
        quality
      );
    };

    img.onerror = () => reject(new Error('Failed to load image'));

    // Read file as data URL
    const reader = new FileReader();
    reader.onload = (e) => {
      img.src = e.target.result;
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

class ImagesService {
  /**
   * Upload an image for a cake order
   * @param {File} file - The image file to upload
   * @param {string} orderId - The cake order ID
   * @param {string} orderType - Type of order (default 'cake')
   * @returns {Promise<{imageUrl: string, imageId: string}>}
   */
  async uploadImage(file, orderId, orderType = 'cake') {
    // Validate file
    if (!file || !file.type.startsWith('image/')) {
      throw new Error('Por favor selecciona un archivo de imagen válido');
    }

    // Compress image
    let imageBlob;
    try {
      imageBlob = await compressImage(file);
    } catch (error) {
      Sentry.captureException(error);
      console.error('Compression error:', error);
      throw new Error('Error al comprimir la imagen');
    }

    // Check final size
    if (imageBlob.size > MAX_IMAGE_SIZE) {
      throw new Error('La imagen es demasiado grande. Por favor usa una imagen más pequeña.');
    }

    // Get store ID for multi-tenant isolation
    const storeId = await userService.getUserStoreId();
    const userId = auth.currentUser?.uid;

    if (!storeId) {
      throw new Error('No se pudo determinar la tienda');
    }

    // Delete existing image for this order if any
    await this.deleteImageByOrderId(orderId);

    // Generate unique filename
    const timestamp = Date.now();
    const storagePath = `${STORAGE_PATH}/${storeId}/${orderId}_${timestamp}.jpg`;
    const storageRef = ref(storage, storagePath);

    // Upload to Firebase Storage
    try {
      await uploadBytes(storageRef, imageBlob, {
        contentType: 'image/jpeg',
        customMetadata: {
          orderId,
          orderType,
          storeId,
          uploadedBy: userId || 'unknown'
        }
      });
    } catch (error) {
      Sentry.captureException(error);
      console.error('Upload error:', error);
      throw new Error('Error al subir la imagen');
    }

    // Get download URL
    const imageUrl = await getDownloadURL(storageRef);

    // Save metadata to Firestore
    const imageDoc = {
      orderId,
      orderType,
      storageUrl: imageUrl,
      storagePath,
      uploadedBy: userId || 'unknown',
      uploadedAt: Timestamp.now(),
      store_id: storeId
    };

    const docRef = await addDoc(collection(db, IMAGES_COLLECTION), imageDoc);

    return {
      imageUrl,
      imageId: docRef.id
    };
  }

  /**
   * Get image metadata by order ID
   * @param {string} orderId - The cake order ID
   * @returns {Promise<{imageUrl: string, imageId: string, uploadedAt: Date} | null>}
   */
  async getImageByOrderId(orderId) {
    // Must include store_id filter to satisfy Firestore security rules
    // ("rules are not filters" — queries must provably match rule constraints)
    const storeId = await userService.getUserStoreId();
    const q = query(
      collection(db, IMAGES_COLLECTION),
      where('orderId', '==', orderId),
      where('store_id', '==', storeId)
    );

    const snapshot = await getDocs(q);
    if (snapshot.empty) {
      return null;
    }

    const doc = snapshot.docs[0];
    const data = doc.data();
    return {
      imageId: doc.id,
      imageUrl: data.storageUrl,
      storagePath: data.storagePath,
      uploadedAt: data.uploadedAt?.toDate() || null,
      uploadedBy: data.uploadedBy
    };
  }

  /**
   * Delete image by order ID
   * @param {string} orderId - The cake order ID
   */
  async deleteImageByOrderId(orderId) {
    const existing = await this.getImageByOrderId(orderId);
    if (!existing) return;

    // Delete from Storage
    try {
      const storageRef = ref(storage, existing.storagePath);
      await deleteObject(storageRef);
    } catch (error) {
      // Ignore if file doesn't exist in storage
      if (error.code !== 'storage/object-not-found') {
        Sentry.captureException(error);
        console.error('Error deleting from storage:', error);
      }
    }

    // Delete from Firestore
    await deleteDoc(doc(db, IMAGES_COLLECTION, existing.imageId));
  }

  /**
   * Delete old images (older than CLEANUP_MONTHS)
   * @returns {Promise<{deleted: number, errors: number}>}
   */
  async cleanupOldImages() {
    const cutoffDate = new Date();
    cutoffDate.setMonth(cutoffDate.getMonth() - CLEANUP_MONTHS);
    const cutoffTimestamp = Timestamp.fromDate(cutoffDate);

    const storeId = await userService.getUserStoreId();
    const q = query(
      collection(db, IMAGES_COLLECTION),
      where('store_id', '==', storeId),
      where('uploadedAt', '<', cutoffTimestamp)
    );

    const snapshot = await getDocs(q);
    let deleted = 0;
    let errors = 0;

    for (const docSnap of snapshot.docs) {
      const data = docSnap.data();
      try {
        // Delete from Storage
        if (data.storagePath) {
          const storageRef = ref(storage, data.storagePath);
          await deleteObject(storageRef);
        }
        // Delete from Firestore
        await deleteDoc(doc(db, IMAGES_COLLECTION, docSnap.id));
        deleted++;
      } catch (error) {
        Sentry.captureException(error);
        console.error(`Error deleting image ${docSnap.id}:`, error);
        errors++;
        // If storage file not found, still delete the Firestore doc
        if (error.code === 'storage/object-not-found') {
          try {
            await deleteDoc(doc(db, IMAGES_COLLECTION, docSnap.id));
            deleted++;
            errors--; // Correction: it did succeed
          } catch (e) {
            Sentry.captureException(e);
            console.error(`Error deleting Firestore doc ${docSnap.id}:`, e);
          }
        }
      }
    }

    return { deleted, errors, total: snapshot.size };
  }

  /**
   * Get count of images older than cleanup threshold
   * @returns {Promise<number>}
   */
  async getOldImagesCount() {
    const cutoffDate = new Date();
    cutoffDate.setMonth(cutoffDate.getMonth() - CLEANUP_MONTHS);
    const cutoffTimestamp = Timestamp.fromDate(cutoffDate);

    const storeId = await userService.getUserStoreId();
    const q = query(
      collection(db, IMAGES_COLLECTION),
      where('store_id', '==', storeId),
      where('uploadedAt', '<', cutoffTimestamp)
    );

    const snapshot = await getDocs(q);
    return snapshot.size;
  }
}

const imagesService = new ImagesService();
export default imagesService;
